import { useEffect, useRef } from "react";
import { Instagram, Linkedin, Facebook, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Motion";
import { SITE, SOCIAL_POSTS } from "../lib/data";

const IG_SCRIPT = "https://www.instagram.com/embed.js";

const useInstagramEmbeds = (deps) => {
  useEffect(() => {
    const process = () => {
      if (window.instgrm && window.instgrm.Embeds) {
        window.instgrm.Embeds.process();
      }
    };
    const existing = document.querySelector(`script[src="${IG_SCRIPT}"]`);
    if (!existing) {
      const s = document.createElement("script");
      s.src = IG_SCRIPT;
      s.async = true;
      s.onload = process;
      document.body.appendChild(s);
    } else {
      // give the DOM a beat, then (re)process
      const t = setTimeout(process, 300);
      return () => clearTimeout(t);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

export const SocialFeed = () => {
  const ref = useRef(null);
  useInstagramEmbeds([]);

  return (
    <section data-testid="social-feed" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-t border-[#E5DFD3] pt-8 mb-14">
          <div>
            <p className="overline text-[#C19A6B]">Follow along</p>
            <h2 className="font-serif-lux text-5xl md:text-7xl text-[#1A1A1A] tracking-tight mt-3">
              From the field
            </h2>
            <p className="font-sans-lux text-[#5C5C5C] mt-4 max-w-md">
              Site walks, market notes and launch days — straight from my Instagram and LinkedIn.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a href={SITE.socials.instagram} target="_blank" rel="noreferrer" data-testid="social-ig" aria-label="Instagram"
              className="w-12 h-12 border border-[#E5DFD3] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors">
              <Instagram size={18} />
            </a>
            <a href={SITE.socials.linkedin} target="_blank" rel="noreferrer" data-testid="social-li" aria-label="LinkedIn"
              className="w-12 h-12 border border-[#E5DFD3] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors">
              <Linkedin size={18} />
            </a>
            <a href={SITE.socials.facebook} target="_blank" rel="noreferrer" data-testid="social-fb" aria-label="Facebook"
              className="w-12 h-12 border border-[#E5DFD3] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors">
              <Facebook size={18} />
            </a>
          </div>
        </div>
      </Reveal>

      <div ref={ref} className="grid md:grid-cols-3 gap-8 items-start">
        {SOCIAL_POSTS.map((p, i) => (
          <Reveal key={p.url} delay={i * 0.1}>
            <div className="bg-[#FDFBF7] border border-[#E5DFD3] overflow-hidden">
              <blockquote
                className="instagram-media"
                data-instgrm-permalink={`${p.url}?utm_source=ig_embed`}
                data-instgrm-version="14"
                style={{ background: "#FFF", margin: 0, padding: 0, width: "100%", minWidth: "unset" }}
              />
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 text-center">
          <a
            href={SITE.socials.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="social-follow-cta"
            className="inline-flex items-center gap-3 border border-[#1A1A1A] text-[#1A1A1A] hover:text-[#FDFBF7] btn-lux px-9 py-4 overline"
          >
            Follow on Instagram <ArrowUpRight size={15} />
          </a>
        </div>
      </Reveal>
    </section>
  );
};
